import React, { useState } from "react";
import { FaProjectDiagram, FaEye, FaNetworkWired } from "react-icons/fa";
import { AiOutlineBarChart } from "react-icons/ai";
import Image1 from "../images/s3.png";
import Image2 from "../images/s6.png";
import Image3 from "../images/s2.png";
import Image4 from "../images/s12.png";

const navItems = [
  { icon: <FaProjectDiagram />, title: "Workflow",     key: "Workflows"    },
  { icon: <AiOutlineBarChart />, title: "AI & Analytics", key: "AI & Analytics" },
  { icon: <FaEye />,            title: "Visibility",   key: "Visibility"   },
  { icon: <FaNetworkWired />,   title: "IoT",          key: "IoT"          },
];

const sections = [
  {
    key: "Workflows",
    title: "Workflows",
    desc: "Optimize tasks like job sheets, vehicle servicing, consignment management, and vehicle indenting for efficient workflows. Our solutions eliminate bottlenecks and ensure seamless operations.",
    img: Image1,
    links: [
      "Multi-Modal Transportation Management System",
      "Predictive Maintenance & Diagnostics",
      "Load/Dispatch Planner",
    ],
  },
  {
    key: "AI & Analytics",
    title: "AI & Analytics",
    desc: "Our AI-driven analytics deliver actionable insights into driver behavior, trip efficiency, and cost optimization. Make smarter, data-backed decisions with advanced analytics.",
    img: Image2,
    links: [
      "Driver Behaviour Monitoring",
      "Fuel Theft Monitoring",
      "Vehicle/Trip P&L Monitoring",
    ],
  },
  {
    key: "Visibility",
    title: "Visibility",
    desc: "Get end-to-end visibility across your workforce, vehicles, assets, and facilities. Real-time tracking and monitoring empower you to stay in control, always.",
    img: Image3,
    links: [
      "GPS Fleet Tracking",
      "Empower EVs with Intelligent Telematics",
      "AI-Powered Video Telematics",
    ],
  },
  {
    key: "IoT",
    title: "IoT",
    desc: "Unlock the power of IoT to enhance safety, optimize fuel consumption, improve vehicle health, and secure your cargo. Future-proof your fleet with connected intelligence.",
    img: Image4,
    links: [
      "Unlocking Cargo Security",
      "Fuel Management Solution",
      "OBD/CAN Based Tracking",
    ],
  },
];

function Operation() {
  const [activeKey, setActiveKey] = useState(null); // null = show all

  const visibleSections =
    activeKey === null
      ? sections
      : sections.filter((s) => s.key === activeKey);

  return (
    <>
      <style>{`
        /* ── Base ── */
        .op-wrapper {
          padding: 3rem 1rem;
          font-family: 'Segoe UI', sans-serif;
        }

        /* ── Heading underline ── */
        .op-underline {
          width: 80px;
          height: 4px;
          background: #007bff;
          border-radius: 2px;
          margin: 10px auto 0;
        }

        /* ── Nav pills ── */
        .op-nav {
          display: flex;
          flex-wrap: wrap;
          justify-content: center;
          gap: 1rem;
          margin-bottom: 3rem;
          list-style: none;
          padding: 0;
        }
        .op-nav-card {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding: 0.75rem 1.25rem;
          border-radius: 10px;
          border: 2px solid transparent;
          background: #fff;
          box-shadow: 0 2px 8px rgba(0,0,0,0.08);
          cursor: pointer;
          min-width: 180px;
          transition: transform 0.25s ease, box-shadow 0.25s ease, background 0.25s ease;
          outline: none;
          text-align: left;
        }
        .op-nav-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 8px 20px rgba(0,0,0,0.13);
        }
        .op-nav-card.active {
          background: #007bff;
          border-color: #0056cc;
          color: #fff;
        }
        .op-nav-card .nav-label {
          font-weight: 600;
          font-size: 0.95rem;
        }
        .op-nav-card .nav-sub {
          font-size: 0.78rem;
          color: #6c757d;
        }
        .op-nav-card.active .nav-sub {
          color: rgba(255,255,255,0.75);
        }
        .op-nav-icon {
          font-size: 1.4rem;
          flex-shrink: 0;
        }

        /* ── Reset filter button ── */
        .op-reset-btn {
          display: block;
          margin: -1.5rem auto 2.5rem;
          background: none;
          border: 1.5px solid #007bff;
          color: #007bff;
          border-radius: 50px;
          padding: 0.3rem 1.1rem;
          font-size: 0.82rem;
          cursor: pointer;
          transition: background 0.2s ease, color 0.2s ease;
        }
        .op-reset-btn:hover {
          background: #007bff;
          color: #fff;
        }

        /* ── Section row ── */
        .op-section {
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          gap: 2rem;
          padding: 3rem 1.5rem;
          border-radius: 14px;
          margin-bottom: 2.5rem;
        }
        .op-section.even {
          background: #fff;
          flex-direction: row;
        }
        .op-section.odd {
          background: #f8f9fa;
          flex-direction: row-reverse;
        }

        /* ── Text column ── */
        .op-text {
          flex: 1 1 280px;
        }
        .op-text h3 {
          font-size: clamp(1.3rem, 3vw, 1.7rem);
          font-weight: 700;
          color: #007bff;
          margin-bottom: 0.75rem;
        }
        .op-text p {
          color: #6c757d;
          font-size: 0.96rem;
          line-height: 1.75;
          margin-bottom: 1rem;
        }

        /* ── Feature links ── */
        .op-links {
          list-style: none;
          padding: 0;
          margin: 0;
        }
        .op-links li {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          margin-bottom: 0.6rem;
          font-size: 0.93rem;
        }
        .op-links a {
          color: #333;
          text-decoration: none;
          transition: color 0.2s ease;
        }
        .op-links a:hover {
          color: #007bff;
        }
        .op-check {
          color: #28a745;
          flex-shrink: 0;
        }

        /* ── Image column ── */
        .op-img-wrap {
          flex: 1 1 280px;
          display: flex;
          justify-content: center;
        }

        /* ── Responsive image ── */
        .op-img {
          width: 100%;
          max-width: 420px;
          height: auto;
          display: block;
          border-radius: 14px;
          box-shadow: 0 8px 28px rgba(0,0,0,0.12);
          object-fit: cover;
          transition: transform 0.35s ease, box-shadow 0.35s ease;
        }
        .op-img:hover {
          transform: scale(1.04);
          box-shadow: 0 14px 40px rgba(0,0,0,0.18);
        }

        /* ── Breakpoints ── */
        @media (max-width: 575px) {
          .op-section,
          .op-section.odd {
            flex-direction: column;
          }
          .op-img {
            max-width: 100%;
            border-radius: 10px;
          }
          .op-nav-card {
            min-width: 140px;
            padding: 0.6rem 0.9rem;
          }
        }

        @media (min-width: 576px) and (max-width: 768px) {
          .op-img { max-width: 100%; }
        }

        @media (min-width: 769px) and (max-width: 1024px) {
          .op-img { max-width: 380px; }
        }

        @media (min-width: 1025px) {
          .op-img { max-width: 420px; }
        }
      `}</style>

      <div className="op-wrapper">
        {/* ── Heading ── */}
        <div className="text-center mb-5">
          <h2 className="fw-bold text-dark">
            Enhance Safety, Efficiency &amp; Sustainability in Fleet Operations
          </h2>
          <div className="op-underline" />
        </div>

        {/* ── Nav Tabs ── */}
        <ul className="op-nav" role="tablist">
          {navItems.map((item) => (
            <li key={item.key}>
              <button
                className={`op-nav-card ${activeKey === item.key ? "active" : ""}`}
                onClick={() =>
                  setActiveKey((prev) => (prev === item.key ? null : item.key))
                }
                aria-selected={activeKey === item.key}
                role="tab"
              >
                <span className="op-nav-icon">{item.icon}</span>
                <div>
                  <div className="nav-label">{item.title}</div>
                  <div className="nav-sub">Learn more →</div>
                </div>
              </button>
            </li>
          ))}
        </ul>

        {/* ── Reset filter (shown only when a tab is active) ── */}
        {activeKey !== null && (
          <button className="op-reset-btn" onClick={() => setActiveKey(null)}>
            ✕ Show all sections
          </button>
        )}

        {/* ── Content Sections ── */}
        {visibleSections.map((sec, i) => {
          const originalIndex = sections.findIndex((s) => s.key === sec.key);
          return (
            <div
              key={sec.key}
              className={`op-section ${originalIndex % 2 === 0 ? "even" : "odd"}`}
            >
              {/* Text */}
              <div className="op-text">
                <h3>{sec.title}</h3>
                <p>{sec.desc}</p>
                <ul className="op-links">
                  {sec.links.map((link) => (
                    <li key={link}>
                      <span className="op-check">✔</span>
                      <a
                        href="#"
                        onClick={(e) => e.preventDefault()}
                      >
                        {link} →
                      </a>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Image */}
              <div className="op-img-wrap">
                <img
                  src={sec.img}
                  alt={sec.title}
                  className="op-img"
                  loading="lazy"
                />
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}

export default Operation;